import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useNavigate } from 'react-router-dom';
import { STUDIO_V2_FLOW_TEMPLATES } from './studioV2FlowTemplates';
import { STUDIO_V2_APP_TEMPLATES } from './studioV2AppTemplates';
import { STUDIO_V2_FORM_TEMPLATES, STUDIO_V2_USE_CASE_BUNDLES } from './studioV2FormTemplates';
import {
  createAppFromTemplateEntry,
  createFlowFromTemplateEntry,
  createFormFromTemplateEntry,
  createUseCaseBundle,
} from './studioV2StarterHelpers';
import { recordFriendlinessEvent } from '../../utils/studioV2FriendlinessSignals.js';

const FLOW_CAT_LABEL = { foundations: 'Foundations', patterns: 'Patterns' };
const APP_CAT_LABEL = { vertical: 'Vertical', layout: 'Layouts' };
const FORM_CAT_LABEL = { recruiting: 'Recruiting' };

/**
 * @param {'full' | 'compact'} variant
 * @param {'both' | 'flows' | 'apps'} mode
 * @param {(msg: string | null) => void} [onError]
 * @param {string} [flowSectionId] element id for hash links (#starter-flows)
 * @param {string} [appSectionId] element id for hash links (#starter-apps)
 */
export default function StudioV2StarterGallery({
  variant = 'full',
  mode = 'both',
  onError,
  flowSectionId,
  appSectionId,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [creatingId, setCreatingId] = useState(null);
  const [flowCat, setFlowCat] = useState('all');
  const [appCat, setAppCat] = useState('all');
  const [formCat, setFormCat] = useState('all');

  const flowTemplates = useMemo(() => {
    if (flowCat === 'all') return STUDIO_V2_FLOW_TEMPLATES;
    return STUDIO_V2_FLOW_TEMPLATES.filter((t) => t.category === flowCat);
  }, [flowCat]);

  const appTemplates = useMemo(() => {
    if (appCat === 'all') return STUDIO_V2_APP_TEMPLATES;
    return STUDIO_V2_APP_TEMPLATES.filter((t) => t.category === appCat);
  }, [appCat]);

  const formTemplates = useMemo(() => {
    if (formCat === 'all') return STUDIO_V2_FORM_TEMPLATES;
    return STUDIO_V2_FORM_TEMPLATES.filter((t) => t.category === formCat);
  }, [formCat]);

  const runFlow = async (tpl) => {
    setCreatingId(`flow_${tpl.id}`);
    onError?.(null);
    const { flow, error } = await createFlowFromTemplateEntry(tpl);
    setCreatingId(null);
    if (error) {
      onError?.(error);
      return;
    }
    if (flow?._id) {
      recordFriendlinessEvent(`starter_template_flow:${tpl.id}`);
      navigate(`/studio/flows/${flow._id}`);
    }
  };

  const runApp = async (tpl) => {
    setCreatingId(`app_${tpl.id}`);
    onError?.(null);
    const { app, error } = await createAppFromTemplateEntry(tpl);
    setCreatingId(null);
    if (error) {
      onError?.(error);
      return;
    }
    if (app?._id) {
      recordFriendlinessEvent(`starter_template_app:${tpl.id}`);
      navigate(`/studio/apps/${app._id}`);
    }
  };

  const runForm = async (tpl) => {
    setCreatingId(`form_${tpl.id}`);
    onError?.(null);
    const { error } = await createFormFromTemplateEntry(tpl);
    setCreatingId(null);
    if (error) {
      onError?.(error);
      return;
    }
    recordFriendlinessEvent(`starter_template_form:${tpl.id}`);
    navigate('/studio/forms/design');
  };

  const runBundle = async (bundle) => {
    setCreatingId(`bundle_${bundle.id}`);
    onError?.(null);
    const result = await createUseCaseBundle(bundle);
    setCreatingId(null);
    if (result.error) {
      onError?.(result.error);
      return;
    }
    recordFriendlinessEvent(`starter_template_bundle:${bundle.id}`);
    if (result.app?.app?._id) navigate(`/studio/apps/${result.app.app._id}`);
    else navigate('/studio');
  };

  const chipSx = { textTransform: 'none', fontWeight: 600 };
  const isCompact = variant === 'compact';

  const filterChips = (value, setValue, options, labels) => (
    <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: isCompact ? 1.5 : 2 }}>
      <Chip
        size="small"
        label="All"
        onClick={() => setValue('all')}
        color={value === 'all' ? 'primary' : 'default'}
        variant={value === 'all' ? 'filled' : 'outlined'}
        sx={chipSx}
      />
      {options.map((key) => (
        <Chip
          key={key}
          size="small"
          label={labels[key] || key}
          onClick={() => setValue(key)}
          color={value === key ? 'primary' : 'default'}
          variant={value === key ? 'filled' : 'outlined'}
          sx={chipSx}
        />
      ))}
    </Stack>
  );

  const TemplateCard = ({
    icon: Icon,
    iconColor,
    primary,
    secondary,
    tags,
    busy,
    onUse,
  }) => (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 2,
        transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.35)}`,
        },
      }}
    >
      <CardContent sx={{ p: isCompact ? 1.5 : 2, '&:last-child': { pb: isCompact ? 1.5 : 2 } }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Box
              sx={{
                p: 0.75,
                borderRadius: 1,
                bgcolor: alpha(iconColor || theme.palette.primary.main, 0.12),
                color: iconColor || 'primary.main',
                display: 'flex',
              }}
            >
              <Icon fontSize={isCompact ? 'small' : 'medium'} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant={isCompact ? 'body2' : 'subtitle2'} fontWeight={700}>
                {primary}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {secondary}
              </Typography>
            </Box>
          </Stack>
          {tags?.length ? (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {tags.slice(0, isCompact ? 2 : 4).map((t) => (
                <Chip key={t} label={t} size="small" variant="outlined" sx={{ height: 22, fontSize: 11 }} />
              ))}
            </Stack>
          ) : null}
          <Button
            size="small"
            variant="contained"
            disabled={busy}
            onClick={onUse}
            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {busy ? 'Creating…' : 'Use template'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Stack spacing={isCompact ? 2 : 3}>
      {mode === 'both' && (
        <Box sx={{ scrollMarginTop: '88px' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <ViewModuleIcon fontSize="small" color="primary" />
            <Typography variant={isCompact ? 'subtitle2' : 'subtitle1'} fontWeight={700}>
              Use-case bundles
            </Typography>
          </Stack>
          <Grid container spacing={isCompact ? 1.5 : 2}>
            {STUDIO_V2_USE_CASE_BUNDLES.map((bundle) => (
              <Grid item xs={12} sm={isCompact ? 12 : 6} md={isCompact ? 12 : 4} key={bundle.id}>
                <TemplateCard
                  icon={ViewModuleIcon}
                  iconColor={theme.palette.primary.main}
                  primary={bundle.label}
                  secondary={bundle.description}
                  tags={bundle.tags}
                  busy={creatingId === `bundle_${bundle.id}`}
                  onUse={() => runBundle(bundle)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      {(mode === 'both' || mode === 'flows') && (
        <Box id={flowSectionId || undefined} sx={{ scrollMarginTop: '88px' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <AccountTreeIcon fontSize="small" color="primary" />
            <Typography variant={isCompact ? 'subtitle2' : 'subtitle1'} fontWeight={700}>
              Flow starters
            </Typography>
          </Stack>
          {filterChips(flowCat, setFlowCat, ['foundations', 'patterns'], FLOW_CAT_LABEL)}
          <Grid container spacing={isCompact ? 1.5 : 2}>
            {flowTemplates.map((tpl) => (
              <Grid item xs={12} sm={isCompact ? 12 : 6} md={isCompact ? 12 : 4} key={tpl.id}>
                <TemplateCard
                  icon={AccountTreeIcon}
                  iconColor={theme.palette.primary.main}
                  primary={tpl.label}
                  secondary={tpl.description}
                  tags={tpl.tags}
                  busy={creatingId === `flow_${tpl.id}`}
                  onUse={() => runFlow(tpl)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {(mode === 'both' || mode === 'apps') && (
        <Box id={appSectionId || undefined} sx={{ scrollMarginTop: '88px' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <ViewModuleIcon fontSize="small" color="secondary" />
            <Typography variant={isCompact ? 'subtitle2' : 'subtitle1'} fontWeight={700}>
              App shells
            </Typography>
          </Stack>
          {filterChips(appCat, setAppCat, ['vertical', 'layout'], APP_CAT_LABEL)}
          <Grid container spacing={isCompact ? 1.5 : 2}>
            {appTemplates.map((tpl) => (
              <Grid item xs={12} sm={isCompact ? 12 : 6} md={isCompact ? 12 : 4} key={tpl.id}>
                <TemplateCard
                  icon={ViewModuleIcon}
                  iconColor={theme.palette.secondary?.main || theme.palette.primary.main}
                  primary={tpl.label}
                  secondary={tpl.description}
                  tags={tpl.tags}
                  busy={creatingId === `app_${tpl.id}`}
                  onUse={() => runApp(tpl)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      {mode === 'both' && (
        <Box sx={{ scrollMarginTop: '88px' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <AssignmentIcon fontSize="small" color="success" />
            <Typography variant={isCompact ? 'subtitle2' : 'subtitle1'} fontWeight={700}>
              Form starters
            </Typography>
          </Stack>
          {filterChips(formCat, setFormCat, ['recruiting'], FORM_CAT_LABEL)}
          <Grid container spacing={isCompact ? 1.5 : 2}>
            {formTemplates.map((tpl) => (
              <Grid item xs={12} sm={isCompact ? 12 : 6} md={isCompact ? 12 : 4} key={tpl.id}>
                <TemplateCard
                  icon={AssignmentIcon}
                  iconColor={theme.palette.success.main}
                  primary={tpl.label}
                  secondary={tpl.description}
                  tags={tpl.tags}
                  busy={creatingId === `form_${tpl.id}`}
                  onUse={() => runForm(tpl)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Stack>
  );
}
